import {useContext, useMemo} from 'react';
import {LocalizerContext} from './LocalizerContext';
import {InterpolationInjectPart} from './localesParser';
import {logWarning} from './localizerUtils';

/**
 * Return a method to localize a simple string
 */
export function useLocalizer() {
	const context = useContext(LocalizerContext);
	if (!context) {
		throw new Error('useLocalizer exception: context is not provided');
	}

	const {translations, language, setLanguage} = context;

	return useMemo(() => {
		const t = (code: string, inject?: Record<string, string | number>) => {
			const result = translations[code];

			if (result === undefined) {
				logWarning(`string ${code} is not found`);
				return `[${code}]`;
			}

			if (result.type === 'plain') {
				return result.value;
			}

			if (!inject) {
				logWarning(`string ${code} is interpolated but inject data is not provided`);
				return result.parts.reduce<string>((total, part, index) => {
					if (index % 2) {
						return `${total}[${(part as InterpolationInjectPart).label}]`;
					}
					return total + (part as string);
				}, '');
			}

			const sureInject = inject || {};
			return result.parts.reduce<string>((total, part, index) => {
				if (!(index % 2)) {
					return total + (part as string);
				}

				const label = (part as InterpolationInjectPart).label;
				const value = sureInject[label] || `[${label}]`;
				return `${total}${value}`;
			}, '');
		};

		// TODO think about it, maybe move out to a separated hook
		t.language = language;
		t.setLanguage = setLanguage;

		return t;
	}, [translations, language]);
}

useLocalizer.displayName = 'useLocalizer';
